<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.delivery_notes.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div class="p-1">
        <label class="form-label" :for="notesId">
          {{ $t("forwards.deals.delivery_notes.label") }}
        </label>
        <textarea
          :id="notesId"
          v-model="notes"
          class="form-control"
          rows="6"
          :maxlength="INTERNAL_NOTES_MAX"
          data-testid="delivery-notes-input"
        />
        <div class="form-text">
          {{ $t("forwards.deals.delivery_notes.help") }}
        </div>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="busy || delivery === null"
        variant="btn-primary"
        @click="submit"
      >
        {{ $t("forwards.deals.delivery_notes.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import type {
  DeliveryNotesRequest,
  DeliveryResponse,
} from "@/forwards2/forwardsTypes";
import { INTERNAL_NOTES_MAX } from "@/forwards2/picker/pickerForm";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * A delivery's staff-only notes — why one shipment differs from the deal, such as its producers'
 * averaged price. A super admin writes them; they are never copied onto the delivery's transaction.
 * The notes are saved whole, so a blank textarea clears them.
 */
const props = withDefaults(
  defineProps<{
    delivery: DeliveryResponse | null;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [request: DeliveryNotesRequest];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const deliveryWindowText = useDeliveryWindowText();
const notesId = useId();

const forceToggle = ref<(value: boolean) => void>();
const notes = ref("");

const title = computed(() =>
  t("forwards.deals.delivery_notes.title", {
    window: props.delivery === null ? "" : deliveryWindowText(props.delivery),
  })
);

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
  if (model.value) notes.value = props.delivery?.internal_notes ?? "";
};

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) notes.value = props.delivery?.internal_notes ?? "";
});

const close = () => {
  model.value = false;
};

const submit = () => {
  if (props.delivery === null) return;
  const text = notes.value.trim();
  emit("submit", { internal_notes: text === "" ? null : notes.value });
};
</script>
